import { useState } from "react";

import { PencilIcon } from "@heroicons/react/24/outline";

import { ILead } from "../../../models/Lead";
import { EngineCondition, IQuote, Status } from "../../../models/Quote";
import { GetQuoteType } from "../../quotes/QuoteCalculationCard";
import Card from "../Card/Card";
import EditQuoteDetailsModal from "./EditQuoteDetailsModal";

// function to add spaces to camelCase strings
function CamelToSpace(str: string) {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function Cell({ title, value }: { title: string, value: string | number | boolean | undefined }) {
  return (
    <div className="p-2 grid grid-cols-3 gap-4">
      <dt className="text-sm font-medium text-gray-500">{title}</dt>
      <dd className="text-sm text-gray-900 col-span-2">
        {value ?? "N/A"}
      </dd>
    </div>
  );
}

/**
 * The seller's answers from the quote form. Given a lead, the answers the yard is told come from
 * the lead instead, because SAV or the yard may have corrected them there - and they can be edited.
 * The quote page passes no lead, so it keeps showing what the seller actually said.
 */
export default function QuoteDetails({
  quote,
  lead,
  cols = "sm:grid-cols-1 divide-y"
} : {
  quote?: IQuote,
  lead?: ILead,
  cols?: string
}) {
  const [editOpen, setEditOpen] = useState(false);

  const answers = lead ?? quote;
  const preferredDate = lead?.preferredCollectionDate
    ? new Date(lead.preferredCollectionDate).toLocaleDateString("en-GB")
    : "N/A";

  const editButton = lead && (
    <button
      type="button"
      onClick={() => setEditOpen(true)}
      className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      title="Edit quote details"
    >
      <PencilIcon className="h-5 w-5" />
    </button>
  );

   return (
    <>
      <Card title="Quote Details" bodyClassName="p-4" headerRight={editButton}>
        <dl className={`divide-gray-200 grid grid-cols-1 ${cols} gap-3`}>
          <Cell title="Quote Type" value={GetQuoteType(quote?.status == Status.AcceptedCollection ? quote?.collectedType : quote?.dropOffType!)}  />
          <Cell title="Reg"        value={quote?.vehicleRegistration} />
          <Cell title="Postcode"   value={quote?.postcode} />
          <Cell title="Voucher"    value={quote?.voucherCode || "N/A"} />

          <Cell title="Engine" value={quote?.engineCondition != null ? CamelToSpace(EngineCondition[quote?.engineCondition]) : "N/A"} />
          <Cell title="Body"   value={quote?.bodyCondition   != null ? CamelToSpace(EngineCondition[quote?.bodyCondition])   : "N/A"} />

          <Cell title="Alloy Wheels"      value={quote?.alloyWheels       ? "Yes"          : "No"}  />
          <Cell title="Collection"        value={quote?.selectedDropOff   ? "No"           : "Yes"} />
          <Cell title="Access To Vehicle" value={answers?.accessToVehicle ? "Yes"          : "No"}  />
          <Cell title="V5C"               value={answers?.v5C             ? "Yes"          : "No"}  />
          <Cell title="Keys"              value={answers?.keys != null    ? answers.keys   : "N/A"} />
          <Cell title="Roadside Payment"  value={answers?.roadSidePayment ? "Yes"          : "No"}  />
          <Cell title="Fast Payment"      value={answers?.fastPayment     ? "Yes"          : "No"}  />
          {lead && (
            <Cell title="Preferred Collection" value={preferredDate} />
          )}
        </dl>
        {/* Full width of its own: identifiers are long enough to wreck the grid columns. */}
        {quote?.gclid && (
          <dl className="mt-3 pt-3 border-t border-gray-200">
            <div className="p-2 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-gray-500">Google Click ID</dt>
              <dd className="text-sm text-gray-900 col-span-2 font-mono break-all select-all">{quote.gclid}</dd>
            </div>
          </dl>
        )}
      </Card>
      {lead && <EditQuoteDetailsModal lead={lead} open={editOpen} setOpen={setEditOpen} />}
    </>
  );
}
